<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import {
  fetchAdCreatives,
  fetchAdCreativeById,
  createAdCreative,
  updateAdCreative,
  deleteAdCreative,
} from "@/services/backofficeClient";
import { ROLES } from "@/services/acl";
import { PLACEMENTS, placementLabel } from "@/services/adPlacements";
import { COUNTRIES, countryFlagEmoji } from "@/data/countries";

const PAGE_SIZE = 25;
const UNLIMITED = -1;

const GENDERS = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "nonBinary", label: "Non-binary" },
];

const MEDIA_TYPES = [
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
];

const route = useRoute();
const store = useStore();
const canCreate = computed(() => store.getters.hasRole(ROLES.ADS_CREATE));
const canEdit = computed(() => store.getters.hasRole(ROLES.ADS_EDIT));
const canDelete = computed(() => store.getters.hasRole(ROLES.ADS_DELETE));

const creatives = ref([]);
const listLoading = ref(true);
const listError = ref("");
const enabledFilter = ref("");
const currentPage = ref(1);
const pagination = ref({
  page: 1,
  pageSize: PAGE_SIZE,
  totalItems: 0,
  totalPages: 0,
  hasNext: false,
});

const selectedId = ref(null);
const saving = ref(false);
const deleting = ref(false);
const formMessage = ref("");
const formMessageType = ref("success");

const form = reactive({
  enabled: true,
  placements: [],
  title: {},
  body: {},
  ctaText: {},
  ctaDestinationUrl: "",
  mediaType: "image",
  mediaUrl: "",
  mediaWidth: "",
  mediaHeight: "",
  mediaDurationMs: "",
  weight: 1,
  minWatchMs: "",
  maxImpressionsPerSession: UNLIMITED,
  maxImpressionsPerDay: UNLIMITED,
  maxImpressionsTotal: UNLIMITED,
  minIntervalSeconds: UNLIMITED,
  criteriaCountryCodes: [],
  criteriaMinAge: "",
  criteriaMaxAge: "",
  criteriaGenders: [],
  criteriaGeoLatitude: "",
  criteriaGeoLongitude: "",
  criteriaGeoRadiusKm: "",
  startsAt: "",
  endsAt: "",
});

const isEditing = computed(() => Boolean(selectedId.value));
const canSubmit = computed(() =>
  isEditing.value ? canEdit.value : canCreate.value,
);
const isEmptyList = computed(
  () => !listLoading.value && !listError.value && creatives.value.length === 0,
);
const totalPages = computed(() =>
  Math.max(pagination.value.totalPages || 0, 1),
);
const isRewardedSelected = computed(() =>
  form.placements.includes("matchRewarded"),
);
const swipeCardNeedsImage = computed(
  () =>
    form.placements.includes("matchingSwipeCard") && form.mediaType === "video",
);
const mediaPreviewUrl = computed(() =>
  /^https?:\/\//i.test(form.mediaUrl.trim()) ? form.mediaUrl.trim() : "",
);

const countryName = (code) =>
  COUNTRIES.find((country) => country.code === code)?.name || code;

const countryLabel = (code) =>
  `${countryFlagEmoji(code)} ${countryName(code)}`.trim();

const genderLabel = (value) =>
  GENDERS.find((gender) => gender.value === value)?.label || value;

const availableCountries = computed(() =>
  COUNTRIES.filter(
    (country) => !form.criteriaCountryCodes.includes(country.code),
  ),
);

const criteriaSummary = (criteria) => {
  if (!criteria) return "";
  const parts = [];
  if (criteria.countryCodes?.length) {
    parts.push(criteria.countryCodes.join(", "));
  }
  if (criteria.minAge != null || criteria.maxAge != null) {
    parts.push(`age ${criteria.minAge ?? "any"}–${criteria.maxAge ?? "any"}`);
  }
  if (criteria.genders?.length) {
    parts.push(criteria.genders.map(genderLabel).join(", "));
  }
  if (criteria.geo) {
    parts.push(`within ${criteria.geo.radiusKm} km`);
  }
  return parts.join(" · ");
};

const activeContentLanguage = ref("");

const contentLanguages = computed(() => {
  const pool = new Set();
  (store.getters.supportedLanguages || []).forEach((code) => {
    const language = normalizeLanguage(code);
    if (language) pool.add(language);
  });
  [form.title, form.body, form.ctaText].forEach((map) =>
    Object.keys(map || {}).forEach((code) => {
      const language = normalizeLanguage(code);
      if (language) pool.add(language);
    }),
  );
  if (!pool.size) pool.add(store.getters.preferredLanguage);
  return [...pool].sort();
});

const normalizeLanguage = (value) => String(value ?? "").trim().toLowerCase();

const normalizeLocalized = (value) => {
  if (typeof value === "string") {
    return value ? { [store.getters.preferredLanguage]: value } : {};
  }
  if (!value || typeof value !== "object") return {};
  return Object.entries(value).reduce((acc, [language, text]) => {
    const code = normalizeLanguage(language);
    if (code) acc[code] = String(text ?? "");
    return acc;
  }, {});
};

const trimLocalized = (value) =>
  Object.entries(value || {}).reduce((acc, [language, text]) => {
    const code = normalizeLanguage(language);
    const trimmed = String(text ?? "").trim();
    if (code && trimmed) acc[code] = trimmed;
    return acc;
  }, {});

const localizedPreview = (value) => {
  const map = normalizeLocalized(value);
  return (
    map[store.getters.preferredLanguage] ||
    Object.values(map).find((text) => text) ||
    ""
  );
};

const contentValue = (field) => form[field]?.[activeContentLanguage.value] ?? "";

const setContentValue = (field, value) => {
  const language = activeContentLanguage.value;
  if (!language) return;
  form[field] = { ...form[field], [language]: value };
};

const languageHasContent = (language) =>
  Boolean(String(form.title?.[language] ?? "").trim()) ||
  Boolean(String(form.ctaText?.[language] ?? "").trim());

const ensureActiveContentLanguage = () => {
  const languages = contentLanguages.value;
  if (languages.includes(activeContentLanguage.value)) return;
  activeContentLanguage.value =
    languages.find(
      (language) => language === store.getters.preferredLanguage,
    ) ||
    languages[0] ||
    "";
};

const toCapValue = (value) => {
  if (value === "" || value === null || value === undefined) return UNLIMITED;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : UNLIMITED;
};

const toNullableInt = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
};

const toNullableNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toInputValue = (value) =>
  value === null || value === undefined ? "" : String(value);

const toLocalInput = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return [
    date.getFullYear(),
    "-",
    pad(date.getMonth() + 1),
    "-",
    pad(date.getDate()),
    "T",
    pad(date.getHours()),
    ":",
    pad(date.getMinutes()),
  ].join("");
};

const fromLocalInput = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const flightLabel = (creative) => {
  if (!creative.startsAt && !creative.endsAt) return "Always on";
  return `${formatDate(creative.startsAt)} → ${formatDate(creative.endsAt)}`;
};

const resetForm = () => {
  selectedId.value = null;
  form.enabled = true;
  form.placements = [];
  form.title = {};
  form.body = {};
  form.ctaText = {};
  form.ctaDestinationUrl = "";
  form.mediaType = "image";
  form.mediaUrl = "";
  form.mediaWidth = "";
  form.mediaHeight = "";
  form.mediaDurationMs = "";
  form.weight = 1;
  form.minWatchMs = "";
  form.maxImpressionsPerSession = UNLIMITED;
  form.maxImpressionsPerDay = UNLIMITED;
  form.maxImpressionsTotal = UNLIMITED;
  form.minIntervalSeconds = UNLIMITED;
  form.criteriaCountryCodes = [];
  form.criteriaMinAge = "";
  form.criteriaMaxAge = "";
  form.criteriaGenders = [];
  form.criteriaGeoLatitude = "";
  form.criteriaGeoLongitude = "";
  form.criteriaGeoRadiusKm = "";
  form.startsAt = "";
  form.endsAt = "";
  formMessage.value = "";
  formMessageType.value = "success";
  ensureActiveContentLanguage();
};

const applyCreativeToForm = (creative) => {
  const content = creative?.content || {};
  const media = content.media || {};
  const cap = creative?.frequencyCap || {};
  const criteria = creative?.criteria || {};
  const geo = criteria.geo || {};

  selectedId.value = creative?.id || null;
  form.enabled = creative?.enabled !== false;
  form.placements = Array.isArray(creative?.placements)
    ? [...creative.placements]
    : [];
  form.title = normalizeLocalized(content.title);
  form.body = normalizeLocalized(content.body);
  form.ctaText = normalizeLocalized(content.ctaText);
  form.ctaDestinationUrl = content.ctaDestinationUrl || "";
  form.mediaType =
    media.type && media.type !== "unknown" ? media.type : "image";
  form.mediaUrl = media.url || "";
  form.mediaWidth = toInputValue(media.width);
  form.mediaHeight = toInputValue(media.height);
  form.mediaDurationMs = toInputValue(media.durationMs);
  form.weight = creative?.weight ?? 1;
  form.minWatchMs = toInputValue(creative?.minWatchMs);
  form.maxImpressionsPerSession = toInputValue(cap.maxImpressionsPerSession);
  form.maxImpressionsPerDay = toInputValue(cap.maxImpressionsPerDay);
  form.maxImpressionsTotal = toInputValue(cap.maxImpressionsTotal);
  form.minIntervalSeconds = toInputValue(cap.minIntervalSeconds);
  form.criteriaCountryCodes = Array.isArray(criteria.countryCodes)
    ? [...criteria.countryCodes]
    : [];
  form.criteriaMinAge = toInputValue(criteria.minAge);
  form.criteriaMaxAge = toInputValue(criteria.maxAge);
  form.criteriaGenders = Array.isArray(criteria.genders)
    ? [...criteria.genders]
    : [];
  form.criteriaGeoLatitude = toInputValue(geo.latitude);
  form.criteriaGeoLongitude = toInputValue(geo.longitude);
  form.criteriaGeoRadiusKm = toInputValue(geo.radiusKm);
  form.startsAt = toLocalInput(creative?.startsAt);
  form.endsAt = toLocalInput(creative?.endsAt);
  formMessage.value = "";
  formMessageType.value = "success";
  ensureActiveContentLanguage();
};

const loadCreatives = async (page = currentPage.value) => {
  listLoading.value = true;
  listError.value = "";
  try {
    const payload = await fetchAdCreatives({
      page,
      pageSize: PAGE_SIZE,
      enabled:
        enabledFilter.value === "" ? null : enabledFilter.value === "true",
    });
    creatives.value = Array.isArray(payload?.items) ? payload.items : [];
    pagination.value = payload?.pagination || {
      page,
      pageSize: PAGE_SIZE,
      totalItems: creatives.value.length,
      totalPages: creatives.value.length ? 1 : 0,
      hasNext: false,
    };
    currentPage.value = pagination.value.page || page;
  } catch (error) {
    console.error("Failed to fetch ad creatives", error);
    listError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load ad creatives.";
  } finally {
    listLoading.value = false;
  }
};

const changePage = (delta) => {
  const nextPage = currentPage.value + delta;
  if (nextPage < 1 || nextPage > totalPages.value) return;
  loadCreatives(nextPage);
};

const applyFilter = () => {
  currentPage.value = 1;
  loadCreatives(1);
};

const selectCreative = async (creative) => {
  if (!creative?.id) return;
  selectedId.value = creative.id;
  formMessage.value = "";
  saving.value = false;
  try {
    const full = await fetchAdCreativeById(creative.id);
    applyCreativeToForm(full);
  } catch (error) {
    console.error("Failed to fetch ad creative", error);
    formMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load creative details.";
    formMessageType.value = "danger";
  }
};

const togglePlacement = (value) => {
  const index = form.placements.indexOf(value);
  if (index === -1) {
    form.placements.push(value);
  } else {
    form.placements.splice(index, 1);
  }
};

const addCountry = (event) => {
  const code = event.target.value;
  event.target.value = "";
  if (code && !form.criteriaCountryCodes.includes(code)) {
    form.criteriaCountryCodes.push(code);
  }
};

const removeCountry = (code) => {
  form.criteriaCountryCodes = form.criteriaCountryCodes.filter(
    (existing) => existing !== code,
  );
};

const toggleGender = (value) => {
  const index = form.criteriaGenders.indexOf(value);
  if (index === -1) {
    form.criteriaGenders.push(value);
  } else {
    form.criteriaGenders.splice(index, 1);
  }
};

const isWebUrl = (value) => {
  try {
    const url = new URL(value);
    return (
      (url.protocol === "http:" || url.protocol === "https:") && !!url.host
    );
  } catch (error) {
    return false;
  }
};

const buildCriteria = () => {
  const minAge = toNullableInt(form.criteriaMinAge);
  const maxAge = toNullableInt(form.criteriaMaxAge);
  for (const [label, value] of [
    ["Min age", minAge],
    ["Max age", maxAge],
  ]) {
    if (value !== null && (value < 1 || value > 120)) {
      throw new Error(`${label} must be between 1 and 120.`);
    }
  }
  if (minAge !== null && maxAge !== null && minAge > maxAge) {
    throw new Error("Min age cannot be greater than max age.");
  }

  const latitude = toNullableNumber(form.criteriaGeoLatitude);
  const longitude = toNullableNumber(form.criteriaGeoLongitude);
  const radiusKm = toNullableNumber(form.criteriaGeoRadiusKm);
  const geoFields = [latitude, longitude, radiusKm];
  let geo = null;
  if (geoFields.some((value) => value !== null)) {
    if (geoFields.some((value) => value === null)) {
      throw new Error(
        "Location needs latitude, longitude and radius, or leave all three empty.",
      );
    }
    if (latitude < -90 || latitude > 90) {
      throw new Error("Latitude must be between -90 and 90.");
    }
    if (longitude < -180 || longitude > 180) {
      throw new Error("Longitude must be between -180 and 180.");
    }
    if (radiusKm <= 0) {
      throw new Error("Radius must be greater than 0 km.");
    }
    geo = { latitude, longitude, radiusKm };
  }

  return {
    countryCodes: [...form.criteriaCountryCodes],
    minAge,
    maxAge,
    genders: [...form.criteriaGenders],
    geo,
  };
};

const buildPayload = () => {
  const title = trimLocalized(form.title);
  const body = trimLocalized(form.body);
  const ctaText = trimLocalized(form.ctaText);
  const ctaDestinationUrl = form.ctaDestinationUrl.trim();
  const mediaUrl = form.mediaUrl.trim();

  if (!form.placements.length) {
    throw new Error("Select at least one placement.");
  }
  if (!Object.keys(title).length) {
    throw new Error("Title is required in at least one language.");
  }
  if (!Object.keys(ctaText).length) {
    throw new Error("CTA text is required in at least one language.");
  }
  if (!isWebUrl(ctaDestinationUrl)) {
    throw new Error("CTA destination URL must be an http(s) URL.");
  }
  if (!isWebUrl(mediaUrl)) {
    throw new Error("Media URL must be an http(s) URL.");
  }

  const weight = toNullableInt(form.weight);
  if (weight === null || weight < 1) {
    throw new Error("Weight must be at least 1.");
  }

  const caps = {
    maxImpressionsPerSession: toCapValue(form.maxImpressionsPerSession),
    maxImpressionsPerDay: toCapValue(form.maxImpressionsPerDay),
    maxImpressionsTotal: toCapValue(form.maxImpressionsTotal),
    minIntervalSeconds: toCapValue(form.minIntervalSeconds),
  };
  for (const [field, value] of Object.entries(caps)) {
    const floor = field === "minIntervalSeconds" ? 0 : 1;
    if (value !== UNLIMITED && value < floor) {
      throw new Error(
        `${field} must be at least ${floor}, or ${UNLIMITED} for unlimited.`
      );
    }
  }

  const startsAt = fromLocalInput(form.startsAt);
  const endsAt = fromLocalInput(form.endsAt);
  if (startsAt && endsAt && new Date(startsAt) >= new Date(endsAt)) {
    throw new Error("Starts at must be before ends at.");
  }

  return {
    enabled: !!form.enabled,
    placements: [...form.placements],
    content: {
      title,
      body,
      ctaText,
      ctaDestinationUrl,
      media: {
        type: form.mediaType,
        url: mediaUrl,
        width: toNullableInt(form.mediaWidth),
        height: toNullableInt(form.mediaHeight),
        durationMs:
          form.mediaType === "video"
            ? toNullableInt(form.mediaDurationMs)
            : null,
      },
    },
    frequencyCap: {
      maxImpressionsPerSession: caps.maxImpressionsPerSession,
      maxImpressionsPerDay: caps.maxImpressionsPerDay,
      maxImpressionsTotal: caps.maxImpressionsTotal,
      minIntervalSeconds: caps.minIntervalSeconds,
    },
    criteria: buildCriteria(),
    weight,
    minWatchMs: toNullableInt(form.minWatchMs),
    startsAt,
    endsAt,
  };
};

const submitForm = async () => {
  formMessage.value = "";
  formMessageType.value = "success";
  saving.value = true;
  try {
    const payload = buildPayload();
    const response = isEditing.value
      ? await updateAdCreative(selectedId.value, payload)
      : await createAdCreative(payload);

    formMessage.value = isEditing.value
      ? "Creative updated."
      : "Creative created.";
    formMessageType.value = "success";

    const newId = response?.id || selectedId.value;
    await loadCreatives();
    if (newId) {
      const fresh = await fetchAdCreativeById(newId).catch(() => response);
      applyCreativeToForm(fresh || response);
      formMessage.value = "Saved.";
    }
  } catch (error) {
    console.error("Failed to save ad creative", error);
    formMessage.value =
      error?.payload?.message || error?.message || "Failed to save creative.";
    formMessageType.value = "danger";
  } finally {
    saving.value = false;
  }
};

const removeCreative = async () => {
  if (!selectedId.value) return;
  const confirmDelete = window.confirm(
    "Are you sure you want to delete this ad creative? Reported events are kept.",
  );
  if (!confirmDelete) return;

  deleting.value = true;
  formMessage.value = "";
  try {
    await deleteAdCreative(selectedId.value);
    resetForm();
    await loadCreatives();
    formMessage.value = "Creative deleted.";
    formMessageType.value = "success";
  } catch (error) {
    console.error("Failed to delete ad creative", error);
    formMessage.value =
      error?.payload?.message || error?.message || "Failed to delete creative.";
    formMessageType.value = "danger";
  } finally {
    deleting.value = false;
  }
};

onMounted(async () => {
  resetForm();
  await loadCreatives(1);
  const requestedId = route.query.creative;
  if (typeof requestedId === "string" && requestedId) {
    await selectCreative({ id: requestedId });
  }
});
</script>

<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12 col-lg-5">
        <div class="card shadow-sm">
          <div
            class="card-header pb-0 border-0 d-flex justify-content-between align-items-start flex-wrap gap-2"
          >
            <div>
              <h6 class="mb-1">Ad creatives</h6>
              <p class="text-xs text-secondary mb-0">
                Select a creative to edit, or start a new one.
              </p>
            </div>
            <div class="d-flex gap-2">
              <button
                v-if="canCreate"
                class="btn btn-sm btn-outline-secondary mb-0"
                type="button"
                :disabled="listLoading"
                @click="resetForm"
              >
                New creative
              </button>
              <button
                class="btn btn-sm btn-outline-success mb-0"
                type="button"
                :disabled="listLoading"
                @click="loadCreatives()"
              >
                <i class="fas fa-sync me-2"></i>
                {{ listLoading ? "Refreshing..." : "Refresh" }}
              </button>
            </div>
          </div>

          <div class="card-body pb-0">
            <label class="form-label text-xs fw-semibold">Status filter</label>
            <select
              v-model="enabledFilter"
              class="form-select form-select-sm"
              :disabled="listLoading"
              @change="applyFilter"
            >
              <option value="">All creatives</option>
              <option value="true">Enabled only</option>
              <option value="false">Disabled only</option>
            </select>
          </div>

          <div v-if="listLoading" class="card-body text-center py-5">
            <div class="spinner-border text-success" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 mb-0 text-sm text-secondary">
              Loading ad creatives...
            </p>
          </div>

          <div v-else-if="listError" class="card-body text-center py-5">
            <div
              class="icon icon-shape bg-gradient-danger text-white shadow text-center border-radius-md mx-auto"
            >
              <i class="ni ni-fat-remove"></i>
            </div>
            <p class="mt-3 text-sm">{{ listError }}</p>
            <button
              class="btn btn-sm btn-success"
              type="button"
              @click="loadCreatives()"
            >
              Try again
            </button>
          </div>

          <div v-else class="card-body">
            <div v-if="isEmptyList" class="text-center py-5">
              <p class="text-sm text-secondary mb-2">No ad creatives yet.</p>
              <button
                v-if="canCreate"
                class="btn btn-sm btn-success"
                type="button"
                @click="resetForm"
              >
                Create first creative
              </button>
            </div>
            <template v-else>
              <div class="table-responsive">
                <table class="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th
                        class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                      >
                        Title
                      </th>
                      <th
                        class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                      >
                        Placements
                      </th>
                      <th
                        class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center"
                      >
                        Weight
                      </th>
                      <th
                        class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center"
                      >
                        Status
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="creative in creatives"
                      :key="creative.id"
                      :class="creative.id === selectedId ? 'bg-light' : ''"
                      style="cursor: pointer"
                      @click="selectCreative(creative)"
                    >
                      <td>
                        <div class="d-flex flex-column">
                          <span class="text-sm font-weight-bold">
                            {{
                              localizedPreview(creative.content?.title) ||
                              "Untitled"
                            }}
                          </span>
                          <span class="text-xs text-secondary">
                            {{ flightLabel(creative) }}
                          </span>
                          <span
                            v-if="criteriaSummary(creative.criteria)"
                            class="text-xs text-info"
                          >
                            {{ criteriaSummary(creative.criteria) }}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          v-for="placement in creative.placements || []"
                          :key="placement"
                          class="badge bg-light text-dark border text-xxs me-1"
                        >
                          {{ placementLabel(placement) }}
                        </span>
                      </td>
                      <td class="text-center">
                        <span class="badge bg-light text-dark border text-xxs">
                          {{ creative.weight ?? 1 }}
                        </span>
                      </td>
                      <td class="text-center">
                        <span
                          class="badge text-xxs"
                          :class="
                            creative.enabled
                              ? 'bg-gradient-success'
                              : 'bg-gradient-secondary'
                          "
                        >
                          {{ creative.enabled ? "Enabled" : "Disabled" }}
                        </span>
                      </td>
                      <td class="text-end">
                        <router-link
                          :to="{ name: 'AdStats', params: { id: creative.id } }"
                          class="text-secondary"
                          title="View statistics"
                          @click.stop
                        >
                          <i class="fas fa-chart-line"></i>
                        </router-link>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                class="d-flex align-items-center flex-wrap gap-2 pt-3 mt-2 border-top"
              >
                <p class="mb-0 text-xs text-secondary">
                  {{ pagination.totalItems }} creative{{
                    pagination.totalItems === 1 ? "" : "s"
                  }}
                </p>
                <div class="ms-auto d-flex align-items-center gap-2">
                  <span class="text-xs text-secondary">
                    Page {{ currentPage }} of {{ totalPages }}
                  </span>
                  <div class="btn-group btn-group-sm">
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      :disabled="listLoading || currentPage <= 1"
                      @click="changePage(-1)"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      :disabled="listLoading || currentPage >= totalPages"
                      @click="changePage(1)"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-7 mt-4 mt-lg-0">
        <div class="card shadow-sm">
          <div
            class="card-header pb-0 border-0 d-flex justify-content-between align-items-start flex-wrap gap-2"
          >
            <div>
              <h6 class="mb-1">
                {{ isEditing ? "Edit ad creative" : "Create ad creative" }}
              </h6>
              <p class="text-xs text-secondary mb-0">
                Creatives are served to the app for the placements you pick
                here. Frequency caps are enforced per user by the server.
              </p>
            </div>
            <div
              v-if="isEditing"
              class="d-flex align-items-center flex-wrap justify-content-end gap-2"
            >
              <router-link
                :to="{ name: 'AdStats', params: { id: selectedId } }"
                class="btn btn-sm btn-outline-primary mb-0"
              >
                <i class="fas fa-chart-line me-2"></i>View statistics
              </router-link>
              <span class="badge bg-light text-dark border text-xxs">
                ID: {{ selectedId }}
              </span>
            </div>
          </div>

          <div class="card-body">
            <div
              v-if="formMessage"
              class="alert py-2 px-3"
              :class="
                formMessageType === 'danger' ? 'alert-danger' : 'alert-success'
              "
            >
              <span class="text-sm">{{ formMessage }}</span>
            </div>

            <div class="form-check form-switch mb-4">
              <input
                id="adCreativeEnabled"
                v-model="form.enabled"
                class="form-check-input"
                type="checkbox"
                :disabled="saving || !canSubmit"
              />
              <label class="form-check-label text-sm" for="adCreativeEnabled">
                Enabled
              </label>
            </div>

            <div class="mb-4">
              <label class="form-label text-sm fw-semibold">Placements</label>
              <div class="d-flex flex-wrap gap-3">
                <div
                  v-for="placement in PLACEMENTS"
                  :key="placement.value"
                  class="form-check"
                >
                  <input
                    :id="`placement-${placement.value}`"
                    class="form-check-input"
                    type="checkbox"
                    :checked="form.placements.includes(placement.value)"
                    :disabled="saving || !canSubmit"
                    @change="togglePlacement(placement.value)"
                  />
                  <label
                    class="form-check-label text-sm"
                    :for="`placement-${placement.value}`"
                  >
                    {{ placement.label }}
                  </label>
                </div>
              </div>
              <p
                v-if="swipeCardNeedsImage"
                class="text-xs text-warning mt-2 mb-0"
              >
                The matching swipe card renders images only &mdash; a video
                creative will be skipped for that placement.
              </p>
            </div>

            <hr />
            <div
              class="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap"
            >
              <h6 class="text-sm mb-0">Content</h6>
              <div class="btn-group btn-group-sm" role="group">
                <button
                  v-for="language in contentLanguages"
                  :key="language"
                  type="button"
                  class="btn"
                  :class="
                    language === activeContentLanguage
                      ? 'btn-primary'
                      : 'btn-outline-secondary'
                  "
                  @click="activeContentLanguage = language"
                >
                  {{ language }}
                  <span v-if="languageHasContent(language)">&#9679;</span>
                </button>
              </div>
            </div>
            <p class="text-xs text-secondary mb-3">
              Copy is per language. A language you leave empty is not sent
              &mdash; the app falls back to one that has copy, so translating
              every language is optional.
            </p>

            <div class="row g-3 mb-3">
              <div class="col-12 col-md-6">
                <label class="form-label text-sm fw-semibold">
                  Title ({{ activeContentLanguage }})
                </label>
                <input
                  :value="contentValue('title')"
                  type="text"
                  class="form-control"
                  placeholder="e.g. Upgrade to Premium"
                  :disabled="saving || !canSubmit || !activeContentLanguage"
                  @input="setContentValue('title', $event.target.value)"
                />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label text-sm fw-semibold">
                  CTA text ({{ activeContentLanguage }})
                </label>
                <input
                  :value="contentValue('ctaText')"
                  type="text"
                  class="form-control"
                  placeholder="e.g. Learn more"
                  :disabled="saving || !canSubmit || !activeContentLanguage"
                  @input="setContentValue('ctaText', $event.target.value)"
                />
              </div>
              <div class="col-12">
                <label class="form-label text-sm fw-semibold">
                  Body ({{ activeContentLanguage }})
                </label>
                <textarea
                  :value="contentValue('body')"
                  class="form-control"
                  rows="2"
                  :disabled="saving || !canSubmit || !activeContentLanguage"
                  @input="setContentValue('body', $event.target.value)"
                ></textarea>
              </div>
              <div class="col-12">
                <label class="form-label text-sm fw-semibold">
                  CTA destination URL
                </label>
                <input
                  v-model="form.ctaDestinationUrl"
                  type="url"
                  class="form-control"
                  placeholder="https://..."
                  :disabled="saving || !canSubmit"
                />
              </div>
            </div>

            <hr />
            <h6 class="text-sm mb-3">Media</h6>

            <div class="row g-3 mb-3">
              <div class="col-12 col-md-4">
                <label class="form-label text-sm fw-semibold">Type</label>
                <select
                  v-model="form.mediaType"
                  class="form-select"
                  :disabled="saving || !canSubmit"
                >
                  <option
                    v-for="mediaType in MEDIA_TYPES"
                    :key="mediaType.value"
                    :value="mediaType.value"
                  >
                    {{ mediaType.label }}
                  </option>
                </select>
              </div>
              <div class="col-12 col-md-8">
                <label class="form-label text-sm fw-semibold">Media URL</label>
                <input
                  v-model="form.mediaUrl"
                  type="url"
                  class="form-control"
                  placeholder="https://cdn..."
                  :disabled="saving || !canSubmit"
                />
              </div>
              <div class="col-6 col-md-4">
                <label class="form-label text-sm fw-semibold">Width (px)</label>
                <input
                  v-model="form.mediaWidth"
                  type="number"
                  min="1"
                  class="form-control"
                  :disabled="saving || !canSubmit"
                />
              </div>
              <div class="col-6 col-md-4">
                <label class="form-label text-sm fw-semibold"
                  >Height (px)</label
                >
                <input
                  v-model="form.mediaHeight"
                  type="number"
                  min="1"
                  class="form-control"
                  :disabled="saving || !canSubmit"
                />
              </div>
              <div v-if="form.mediaType === 'video'" class="col-6 col-md-4">
                <label class="form-label text-sm fw-semibold"
                  >Duration (ms)</label
                >
                <input
                  v-model="form.mediaDurationMs"
                  type="number"
                  min="0"
                  class="form-control"
                  :disabled="saving || !canSubmit"
                />
              </div>
            </div>

            <div v-if="mediaPreviewUrl" class="mb-3">
              <label class="form-label text-xs text-secondary">Preview</label>
              <div class="border rounded p-2">
                <img
                  v-if="form.mediaType === 'image'"
                  :src="mediaPreviewUrl"
                  class="rounded"
                  style="
                    max-height: 180px;
                    max-width: 100%;
                    object-fit: contain;
                  "
                />
                <video
                  v-else
                  :src="mediaPreviewUrl"
                  class="rounded"
                  controls
                  style="max-height: 180px; max-width: 100%"
                ></video>
              </div>
            </div>

            <hr />
            <h6 class="text-sm mb-3">Delivery</h6>

            <div class="row g-3 mb-3">
              <div class="col-6 col-md-3">
                <label class="form-label text-sm fw-semibold">Weight</label>
                <input
                  v-model="form.weight"
                  type="number"
                  min="1"
                  class="form-control"
                  :disabled="saving || !canSubmit"
                />
              </div>
              <div v-if="isRewardedSelected" class="col-6 col-md-3">
                <label class="form-label text-sm fw-semibold">
                  Min watch (ms)
                </label>
                <input
                  v-model="form.minWatchMs"
                  type="number"
                  min="0"
                  class="form-control"
                  placeholder="30000"
                  :disabled="saving || !canSubmit"
                />
              </div>
              <div class="col-12 col-md-3">
                <label class="form-label text-sm fw-semibold">Starts at</label>
                <input
                  v-model="form.startsAt"
                  type="datetime-local"
                  class="form-control"
                  :disabled="saving || !canSubmit"
                />
              </div>
              <div class="col-12 col-md-3">
                <label class="form-label text-sm fw-semibold">Ends at</label>
                <input
                  v-model="form.endsAt"
                  type="datetime-local"
                  class="form-control"
                  :disabled="saving || !canSubmit"
                />
              </div>
            </div>

            <hr />
            <h6 class="text-sm mb-1">Audience criteria</h6>
            <p class="text-xs text-secondary mb-3">
              The creative is only shown to users matching every criterion you
              set. Leave a criterion empty to ignore it. A user missing the
              profile data a criterion needs (no birthdate, gender, country or
              location) is not shown the creative.
            </p>

            <div class="row g-3 mb-3">
              <div class="col-12">
                <label class="form-label text-sm fw-semibold">Countries</label>
                <select
                  class="form-select"
                  :disabled="saving || !canSubmit"
                  @change="addCountry"
                >
                  <option value="">
                    {{
                      form.criteriaCountryCodes.length
                        ? "Add another country..."
                        : "All countries — pick to restrict"
                    }}
                  </option>
                  <option
                    v-for="country in availableCountries"
                    :key="country.code"
                    :value="country.code"
                  >
                    {{ countryLabel(country.code) }}
                  </option>
                </select>
                <div
                  v-if="form.criteriaCountryCodes.length"
                  class="d-flex flex-wrap gap-2 mt-2"
                >
                  <span
                    v-for="code in form.criteriaCountryCodes"
                    :key="code"
                    class="badge bg-light text-dark border text-xs d-inline-flex align-items-center"
                  >
                    {{ countryLabel(code) }}
                    <button
                      v-if="canSubmit"
                      type="button"
                      class="btn-close ms-2"
                      style="font-size: 0.5rem"
                      :aria-label="`Remove ${countryName(code)}`"
                      :disabled="saving"
                      @click="removeCountry(code)"
                    ></button>
                  </span>
                </div>
              </div>

              <div class="col-6 col-md-3">
                <label class="form-label text-sm fw-semibold">Min age</label>
                <input
                  v-model="form.criteriaMinAge"
                  type="number"
                  min="1"
                  max="120"
                  class="form-control"
                  placeholder="Any"
                  :disabled="saving || !canSubmit"
                />
              </div>
              <div class="col-6 col-md-3">
                <label class="form-label text-sm fw-semibold">Max age</label>
                <input
                  v-model="form.criteriaMaxAge"
                  type="number"
                  min="1"
                  max="120"
                  class="form-control"
                  placeholder="Any"
                  :disabled="saving || !canSubmit"
                />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label text-sm fw-semibold">Genders</label>
                <div class="d-flex flex-wrap gap-3 pt-1">
                  <div
                    v-for="gender in GENDERS"
                    :key="gender.value"
                    class="form-check"
                  >
                    <input
                      :id="`criteria-gender-${gender.value}`"
                      class="form-check-input"
                      type="checkbox"
                      :checked="form.criteriaGenders.includes(gender.value)"
                      :disabled="saving || !canSubmit"
                      @change="toggleGender(gender.value)"
                    />
                    <label
                      class="form-check-label text-sm"
                      :for="`criteria-gender-${gender.value}`"
                    >
                      {{ gender.label }}
                    </label>
                  </div>
                </div>
                <p class="text-xs text-secondary mt-1 mb-0">
                  None ticked means every gender.
                </p>
              </div>

              <div class="col-12">
                <label class="form-label text-sm fw-semibold">
                  Within distance of a location
                </label>
                <div class="row g-2">
                  <div class="col-12 col-md-4">
                    <input
                      v-model="form.criteriaGeoLatitude"
                      type="number"
                      step="any"
                      min="-90"
                      max="90"
                      class="form-control"
                      placeholder="Latitude"
                      :disabled="saving || !canSubmit"
                    />
                  </div>
                  <div class="col-12 col-md-4">
                    <input
                      v-model="form.criteriaGeoLongitude"
                      type="number"
                      step="any"
                      min="-180"
                      max="180"
                      class="form-control"
                      placeholder="Longitude"
                      :disabled="saving || !canSubmit"
                    />
                  </div>
                  <div class="col-12 col-md-4">
                    <input
                      v-model="form.criteriaGeoRadiusKm"
                      type="number"
                      step="any"
                      min="0"
                      class="form-control"
                      placeholder="Radius (km)"
                      :disabled="saving || !canSubmit"
                    />
                  </div>
                </div>
                <p class="text-xs text-secondary mt-1 mb-0">
                  Matched against the user's saved location. Fill all three
                  fields, or leave them empty for no distance limit.
                </p>
              </div>
            </div>

            <hr />
            <h6 class="text-sm mb-1">Frequency cap</h6>
            <p class="text-xs text-secondary mb-3">
              -1 means no cap (an empty field is saved as -1). Only the session
              cap is enforced by the client; the rest are backstopped by the
              server.
            </p>

            <div class="row g-3 mb-4">
              <div class="col-6 col-md-3">
                <label class="form-label text-sm fw-semibold"
                  >Per session</label
                >
                <input
                  v-model="form.maxImpressionsPerSession"
                  type="number"
                  min="-1"
                  class="form-control"
                  :disabled="saving || !canSubmit"
                />
              </div>
              <div class="col-6 col-md-3">
                <label class="form-label text-sm fw-semibold">Per day</label>
                <input
                  v-model="form.maxImpressionsPerDay"
                  type="number"
                  min="-1"
                  class="form-control"
                  :disabled="saving || !canSubmit"
                />
              </div>
              <div class="col-6 col-md-3">
                <label class="form-label text-sm fw-semibold">Lifetime</label>
                <input
                  v-model="form.maxImpressionsTotal"
                  type="number"
                  min="-1"
                  class="form-control"
                  :disabled="saving || !canSubmit"
                />
              </div>
              <div class="col-6 col-md-3">
                <label class="form-label text-sm fw-semibold">
                  Min gap (seconds)
                </label>
                <input
                  v-model="form.minIntervalSeconds"
                  type="number"
                  min="-1"
                  class="form-control"
                  :disabled="saving || !canSubmit"
                />
              </div>
            </div>

            <div class="d-flex flex-wrap gap-2">
              <button
                v-if="canSubmit"
                class="btn btn-sm bg-gradient-success mb-0"
                type="button"
                :disabled="saving"
                @click="submitForm"
              >
                {{
                  saving
                    ? "Saving..."
                    : isEditing
                      ? "Save changes"
                      : "Create creative"
                }}
              </button>
              <button
                class="btn btn-sm btn-outline-secondary mb-0"
                type="button"
                :disabled="saving || deleting"
                @click="resetForm"
              >
                Cancel
              </button>
              <button
                v-if="isEditing && canDelete"
                class="btn btn-sm btn-outline-danger mb-0 ms-auto"
                type="button"
                :disabled="deleting || saving"
                @click="removeCreative"
              >
                {{ deleting ? "Deleting..." : "Delete" }}
              </button>
            </div>

            <p v-if="!canSubmit" class="text-xs text-secondary mt-3 mb-0">
              You have read-only access to ad creatives.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
